import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/context/ToastContext";
import { tenantApi, roleManagementApi } from "@/lib/api";
import { Icon } from "@/components/Icon";
import { Badge } from "@/components/Badge";
import { Alert } from "@/components/Alert";
import { PageHeader, Modal, Table, Input, Loading } from "@/components";
import { SearchableMultiSelect } from "@/components/SearchableMultiSelect";
import { Switch } from "@/components/Switch";
import { Select } from "@/components/Select";

export const Menus = () => {
    const { t, i18n } = useTranslation("global");
    const { showError, showSuccess } = useToast();
    const [loading, setLoading] = useState(true);
    const [menusList, setMenusList] = useState([]);
    const [roles, setRoles] = useState([]);
    const [editingMenu, setEditingMenu] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [menuForm, setMenuForm] = useState({
        id: null,
        tag: "",
        name: "",
        key: "",
        path: "",
        icon: "",
        parent: null,
        order: 0,
        is_visible: true,
        show_in_topbar: true,
        roles: [],
    });

    const activeCompanyId = localStorage.getItem("vigepra_active_company");

    const fetchData = async () => {
        try {
            setLoading(true);
            const [menusRes, rolesRes] = await Promise.all([
                tenantApi.getMenus(activeCompanyId),
                roleManagementApi.getAll(),
            ]);

            if (rolesRes.result) {
                setRoles(
                    rolesRes.data.filter(
                        (r) =>
                            r.tag?.toLowerCase() !== "root" &&
                            r.tag?.toLowerCase() !== "admin"
                    )
                );
            }

            if (menusRes.result && Array.isArray(menusRes.data.menus)) {
                setMenusList(menusRes.data.menus);
            }
        } catch (error) {
            showError(error?.message || t("common.error"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeCompanyId && activeCompanyId !== "main") {
            fetchData();
        }
    }, [activeCompanyId]);

    const saveSingleMenu = async (menuItem, overrides = {}) => {
        try {
            const payload = {
                menus: [
                    {
                        id: menuItem.id,
                        tag: menuItem.tag,
                        is_visible:
                            overrides.is_visible !== undefined
                                ? overrides.is_visible
                                : menuItem.is_visible,
                        show_in_topbar:
                            overrides.show_in_topbar !== undefined
                                ? overrides.show_in_topbar
                                : menuItem.show_in_topbar !== false,
                        roles:
                            overrides.roles !== undefined
                                ? overrides.roles
                                : menuItem.roles,
                        order:
                            Number(
                                overrides.order !== undefined
                                    ? overrides.order
                                    : menuItem.order
                            ) || 0,
                        icon:
                            overrides.icon !== undefined
                                ? overrides.icon
                                : menuItem.icon,
                        parent: menuItem.parent
                            ? Number(menuItem.parent)
                            : null,
                        name: menuItem.name || null,
                        key: menuItem.key || null,
                        path: menuItem.path || null,
                    },
                ],
            };

            await tenantApi.updateMenus(activeCompanyId, payload);
            showSuccess(t("common.saved"));
            window.dispatchEvent(new Event("menus_updated"));
        } catch (error) {
            showError(error?.message || t("common.error"));
        }
    };

    const handleOrderChange = (menuId, newOrder) => {
        setMenusList((prev) =>
            prev.map((m) => {
                if (m.id === menuId) {
                    return { ...m, order: newOrder };
                }
                return m;
            })
        );
    };

    const handleOrderBlur = (menuItem) => {
        saveSingleMenu(menuItem);
    };

    const handleOrderKeyDown = (e) => {
        if (e.key === "Enter") {
            e.target.blur();
        }
    };

    const handleModalSave = async () => {
        const newErrors = {};
        if (!menuForm.tag) newErrors.tag = t("common.required");
        if (!menuForm.name) newErrors.name = t("common.required");

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            showError(t("menu.config.create_error_required"));
            return;
        }

        setErrors({});

        try {
            const payload = {
                menus: [
                    {
                        id: menuForm.id,
                        tag: menuForm.tag,
                        name: menuForm.name || null,
                        key: menuForm.key || null,
                        path: menuForm.path || null,
                        icon: menuForm.icon || null,
                        parent: menuForm.parent
                            ? Number(menuForm.parent)
                            : null,
                        order: Number(menuForm.order) || 0,
                        is_visible: Boolean(menuForm.is_visible),
                        show_in_topbar: Boolean(menuForm.show_in_topbar),
                        roles: menuForm.roles || [],
                    },
                ],
            };

            await tenantApi.updateMenus(activeCompanyId, payload);
            showSuccess(
                editingMenu
                    ? t("common.saved")
                    : t("menu.config.create_success")
            );
            setIsModalOpen(false);
            setEditingMenu(null);

            await fetchData();
            window.dispatchEvent(new Event("menus_updated"));
        } catch (error) {
            showError(error?.message || t("common.error"));
        }
    };

    const tableColumns = [
        {
            key: "order",
            label: t("common.order"),
            sortable: false,
            className: "w-24",
            render: (menu) => (
                <input
                    type="number"
                    className="input w-20 text-sm"
                    value={menu.order ?? 0}
                    onChange={(e) => handleOrderChange(menu.id, e.target.value)}
                    onBlur={() => handleOrderBlur(menu)}
                    onKeyDown={(e) => handleOrderKeyDown(e)}
                />
            ),
        },
        {
            key: "menu",
            label: t("common.menu"),
            searchKey: "name",
            sortable: false,
            render: (menu) => {
                const isChild = Boolean(menu.parent);
                const isHeader = !menu.path && !menu.parent;
                return (
                    <div
                        className={`flex items-center gap-2 ${isChild ? "pl-6" : ""}`}
                    >
                        {isChild && (
                            <Icon
                                value="cornerDownRight"
                                size={16}
                                className="text-slate-300 dark:text-slate-600 mr-1"
                            />
                        )}
                        <span className={isHeader ? "font-bold" : ""}>
                            {(() => {
                                if (!menu.key) return menu.name;
                                const tKey = menu.key.startsWith("menu.")
                                    ? menu.key
                                    : `menu.${menu.key}`;
                                return i18n.exists(tKey) ? t(tKey) : menu.name;
                            })()}
                        </span>
                    </div>
                );
            },
        },
        {
            key: "icon",
            label: t("menu.config.icon_label"),
            sortable: false,
            render: (menu) =>
                menu.icon ? (
                    <div className="flex items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-300">
                        <Icon value={menu.icon} size={14} />
                        <div>{menu.icon}</div>
                    </div>
                ) : (
                    ""
                ),
        },
        {
            key: "tag",
            label: t("menu.config.tag_label"),
            sortable: false,
            render: (menu) => menu.tag || "",
        },
        {
            key: "key",
            label: t("menu.config.key_label"),
            sortable: false,
            render: (menu) => menu.key || "",
        },
        {
            key: "path",
            label: t("menu.config.path_label"),
            sortable: false,
            render: (menu) => menu.path || "",
        },
        {
            key: "is_visible",
            align: "center",
            label: t("menu.config.visible_title"),
            sortable: false,
            render: (menu) => (
                <Badge variant={menu.is_visible ? "success" : "error"}>
                    {menu.is_visible ? t("common.yes") : t("common.no")}
                </Badge>
            ),
        },
        {
            key: "show_in_topbar",
            align: "center",
            label: t("menu.config.topbar_title"),
            sortable: false,
            render: (menu) => (
                <Badge variant={menu.show_in_topbar !== false ? "success" : "neutral"}>
                    {menu.show_in_topbar !== false ? t("common.yes") : t("common.no")}
                </Badge>
            ),
        },
        {
            key: "roles",
            label: t("menu.config.allowed_roles"),
            sortable: false,
            render: (menu) => {
                const allowedRoles = Array.isArray(menu.roles)
                    ? menu.roles
                    : [];
                return (
                    <div className="flex flex-wrap gap-2">
                        {roles
                            .filter((r) => allowedRoles.includes(r.id))
                            .map((role) => (
                                <Badge key={role.id} variant="primary">
                                    {(() => {
                                        const tagKey = `roles.tags.${(
                                            role.tag || role.name || ""
                                        ).toLowerCase()}`;
                                        const translated = t(tagKey);
                                        return translated !== tagKey
                                            ? translated
                                            : role.name || role.label || "";
                                    })()}
                                </Badge>
                            ))}
                    </div>
                );
            },
        },
    ];

    const tableActions = [
        {
            label: t("menu.config.add_submenu"),
            icon: (
                <Icon
                    value="plus"
                    size={14}
                    className="text-slate-600 dark:text-zinc-300"
                />
            ),
            onClick: (row) => {
                setErrors({});
                setEditingMenu(null);
                setMenuForm({
                    id: null,
                    tag: "",
                    key: "",
                    path: "",
                    icon: "",
                    parent: Number(row.id),
                    order: 0,
                    is_visible: true,
                    show_in_topbar: true,
                    roles: [],
                });
                setIsModalOpen(true);
            },
        },
        {
            label: t("common.edit"),
            icon: (
                <Icon
                    value="pencil"
                    size={14}
                    className="text-slate-600 dark:text-zinc-300"
                />
            ),
            onClick: (row) => {
                setErrors({});
                setEditingMenu(row);
                setMenuForm({
                    id: row.id,
                    tag: row.tag,
                    name: row.name || "",
                    key: row.key || "",
                    path: row.path || "",
                    icon: row.icon || "",
                    parent: row.parent ? Number(row.parent) : null,
                    order: row.order || 0,
                    is_visible: Boolean(row.is_visible),
                    show_in_topbar: row.show_in_topbar !== false,
                    roles: Array.isArray(row.roles) ? row.roles : [],
                });
                setIsModalOpen(true);
            },
        },
        {
            label: t("common.delete"),
            icon: <Icon value="trash" size={14} className="text-rose-600" />,
            variant: "delete",
            onClick: (row) => {
                const confirmDelete = window.confirm(
                    t("menu.config.delete_confirm")
                );
                if (confirmDelete) {
                    const children = menusList.filter(
                        (m) => Number(m.parent) === Number(row.id)
                    );
                    const payload = [
                        { tag: row.tag, is_visible: false },
                        ...children.map((c) => ({
                            tag: c.tag,
                            is_visible: false,
                        })),
                    ];
                    tenantApi
                        .updateMenus(activeCompanyId, { menus: payload })
                        .then(() => {
                            showSuccess(t("menu.config.delete_success"));
                            fetchData();
                            window.dispatchEvent(new Event("menus_updated"));
                        })
                        .catch(() => showError(t("menu.config.delete_error")));
                }
            },
        },
    ];

    const sortHierarchically = (items, parentId = null) => {
        const nodes = items
            .filter((i) => (i.parent ? Number(i.parent) : null) === parentId)
            .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

        const result = [];
        nodes.forEach((node) => {
            result.push(node);
            sortHierarchically(items, Number(node.id)).forEach((child) =>
                result.push(child)
            );
        });
        return result;
    };

    const hierarchicalMenus = sortHierarchically(menusList).map((m) => {
        const parentName = m.parent
            ? menusList.find((p) => Number(p.id) === Number(m.parent))?.label ||
              ""
            : "";
        const roleNames = roles
            .filter((r) => (m.roles || []).includes(r.id))
            .map((r) => r.tag)
            .join(" ");
        return {
            ...m,
            _searchText: [
                m.key || "",
                parentName,
                m.tag || "",
                m.icon || "",
                roleNames,
            ].join(" "),
        };
    });

    const parentOptions = menusList
        .filter((m) =>
            editingMenu ? Number(m.id) !== Number(editingMenu.id) : true
        )
        .map((m) => {
            let translated = m.name;
            if (m.key) {
                const tKey = m.key.startsWith("menu.")
                    ? m.key
                    : `menu.${m.key}`;
                translated = i18n.exists(tKey) ? t(tKey) : m.name;
            }
            return {
                value: String(m.id),
                label: m.key ? `${translated} (${m.tag})` : m.tag,
            };
        });

    return (
        <div className="page">
            {activeCompanyId === "main" ? (
                <div className="p-8 text-center text-slate-500">
                    {t("menu.config.menus_only_tenant")}
                </div>
            ) : loading ? (
                <Loading />
            ) : (
                <>
                    <PageHeader
                        compact
                        title={<span>{t("menu.config.title")}</span>}
                        subtitle={t("menu.config.description")}
                        action={{
                            label: t("menu.config.add_menu"),
                            icon: "plus",
                            onClick: () => {
                                setErrors({});
                                setEditingMenu(null);
                                setMenuForm({
                                    id: null,
                                    tag: "",
                                    key: "",
                                    path: "",
                                    icon: "",
                                    parent: null,
                                    order: 0,
                                    is_visible: true,
                                    show_in_topbar: true,
                                    roles: [],
                                });
                                setIsModalOpen(true);
                            },
                        }}
                    />

                    <Alert
                        className="mb-4"
                        variant="warning"
                        value={t("menu.config.warning_note")}
                        align="center"
                    />

                    <Table
                        viewKey="vigepra_menus"
                        appearance="workspace"
                        columns={tableColumns}
                        searchKeys={["_searchText"]}
                        data={hierarchicalMenus}
                        actions={tableActions}
                        rowClassName={(row) =>
                            !row.parent && !row.path
                                ? "bg-slate-100 dark:bg-zinc-800/50"
                                : ""
                        }
                    />

                    <Modal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        title={
                            editingMenu
                                ? t("common.edit")
                                : t("menu.config.add_menu")
                        }
                        maxWidth="2xl"
                        onSubmit={handleModalSave}
                        submitLabel={t("common.save")}
                    >
                        <div className="space-y-6">
                            <div
                                className="box-switch cursor-pointer"
                                onClick={(event) => {
                                    const target = event.target;
                                    if (
                                        target.closest("button") ||
                                        target.closest("[data-switch-content]")
                                    ) {
                                        return;
                                    }
                                    setMenuForm((prev) => ({
                                        ...prev,
                                        is_visible: !prev.is_visible,
                                    }));
                                }}
                            >
                                <Switch
                                    checked={menuForm.is_visible}
                                    onChange={(val) =>
                                        setMenuForm((prev) => ({
                                            ...prev,
                                            is_visible: val,
                                        }))
                                    }
                                    label={t("menu.config.visible_title")}
                                    labelClassName="font-bold"
                                    size="md"
                                    position="right"
                                />
                            </div>

                            <div
                                className="box-switch cursor-pointer"
                                onClick={(event) => {
                                    const target = event.target;
                                    if (
                                        target.closest("button") ||
                                        target.closest("[data-switch-content]")
                                    ) {
                                        return;
                                    }
                                    setMenuForm((prev) => ({
                                        ...prev,
                                        show_in_topbar: !prev.show_in_topbar,
                                    }));
                                }}
                            >
                                <Switch
                                    checked={menuForm.show_in_topbar}
                                    onChange={(val) =>
                                        setMenuForm((prev) => ({
                                            ...prev,
                                            show_in_topbar: val,
                                        }))
                                    }
                                    label={t("menu.config.topbar_title")}
                                    labelClassName="font-bold"
                                    size="md"
                                    position="right"
                                />
                            </div>

                            <div>
                                <Select
                                    label={t("menu.config.parent")}
                                    value={
                                        menuForm.parent
                                            ? String(menuForm.parent)
                                            : ""
                                    }
                                    onChange={(val) =>
                                        setMenuForm((prev) => ({
                                            ...prev,
                                            parent: val ? Number(val) : null,
                                        }))
                                    }
                                    options={parentOptions}
                                    full
                                    clearable={true}
                                />
                            </div>

                            <div>
                                <Input
                                    label={t("common.order")}
                                    type="number"
                                    value={menuForm.order}
                                    onChange={(e) =>
                                        setMenuForm((prev) => ({
                                            ...prev,
                                            order:
                                                e.target.value === ""
                                                    ? ""
                                                    : Number(e.target.value),
                                        }))
                                    }
                                />
                            </div>

                            <div>
                                <Input
                                    label={t("common.tag")}
                                    type="text"
                                    required
                                    value={menuForm.tag}
                                    onChange={(e) => {
                                        setMenuForm((prev) => ({
                                            ...prev,
                                            tag: e.target.value,
                                        }));
                                        if (errors.tag)
                                            setErrors((prev) => ({
                                                ...prev,
                                                tag: null,
                                            }));
                                    }}
                                    error={errors.tag}
                                    placeholder={t(
                                        "menu.config.tag_code_placeholder"
                                    )}
                                    disabled={Boolean(editingMenu)}
                                />
                                {Boolean(editingMenu) && (
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 ml-1">
                                        {t("menu.config.tag_hint")}
                                    </p>
                                )}
                            </div>

                            <div>
                                <Input
                                    label={t("common.name")}
                                    type="text"
                                    required
                                    value={menuForm.name}
                                    onChange={(e) => {
                                        setMenuForm((prev) => ({
                                            ...prev,
                                            name: e.target.value,
                                        }));
                                        if (errors.name)
                                            setErrors((prev) => ({
                                                ...prev,
                                                name: null,
                                            }));
                                    }}
                                    error={errors.name}
                                    placeholder={t(
                                        "menu.config.menu_label_placeholder"
                                    )}
                                />
                            </div>

                            <div>
                                <Input
                                    label={t("menu.config.key_label")}
                                    type="text"
                                    value={menuForm.key}
                                    onChange={(e) =>
                                        setMenuForm((prev) => ({
                                            ...prev,
                                            key: e.target.value,
                                        }))
                                    }
                                    placeholder="Ej: menu.custom.ventas"
                                />
                            </div>

                            <div>
                                <Input
                                    label={t("menu.config.path_label")}
                                    type="text"
                                    value={menuForm.path}
                                    onChange={(e) =>
                                        setMenuForm((prev) => ({
                                            ...prev,
                                            path: e.target.value,
                                        }))
                                    }
                                    placeholder={t(
                                        "menu.config.path_placeholder"
                                    )}
                                />
                            </div>

                            <div>
                                <Input
                                    label={t("menu.config.icon_label")}
                                    type="text"
                                    value={menuForm.icon}
                                    onChange={(e) =>
                                        setMenuForm((prev) => ({
                                            ...prev,
                                            icon: e.target.value,
                                        }))
                                    }
                                    placeholder={t(
                                        "menu.config.icon_placeholder"
                                    )}
                                />
                            </div>

                            <div>
                                <SearchableMultiSelect
                                    label={t("menu.config.allowed_roles")}
                                    options={roles}
                                    value={menuForm.roles}
                                    onChange={(val) =>
                                        setMenuForm((prev) => ({
                                            ...prev,
                                            roles: val,
                                        }))
                                    }
                                    placeholder={t("menu.config.search_roles")}
                                />
                            </div>
                        </div>
                    </Modal>
                </>
            )}
        </div>
    );
};
